import React from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setLanguage } from 'i18n';
import { assessmentLoad } from 'actions/assessment';
import config from 'config';

const STORAGE_KEY = 'assessment_lang';

const ASSESSMENT_LANGS = [
  { code: 'ua', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
];

const Wrap = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 24,
});

const StyledSelect = styled(Select)(({ theme }) => ({
  fontFamily: 'Rubik-Regular, system-ui, sans-serif',
  fontSize: 14,
  color: theme.palette.grey[700],
  '& .MuiSelect-select': {
    padding: '6px 28px 6px 12px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[500],
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

function LanguageDropdownInner() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const phasesLoaded = useSelector(
    (state) => Array.isArray(state.assessment?.phases) && state.assessment.phases.length > 0,
  );
  const [lang, setLang] = React.useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && ASSESSMENT_LANGS.some((l) => l.code === stored)) return stored;
    if (i18n.language && ASSESSMENT_LANGS.some((l) => l.code === i18n.language)) return i18n.language;
    return 'ua';
  });

  React.useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && stored !== i18n.language && ASSESSMENT_LANGS.some((l) => l.code === stored)) {
      setLanguage(stored).catch(() => {});
    }
    // Run on mount only — applying the stored choice is a one-shot action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = async (event) => {
    const code = event.target.value;
    setLang(code);
    sessionStorage.setItem(STORAGE_KEY, code);
    await setLanguage(code);
    // If the assessment has already been loaded, re-fetch phases in the new
    // locale. Question IDs are stable across locales, so previously stored
    // answers in `state.answers` remain valid; only question text changes.
    // No reload before phases load — the next assessmentLoad() call will
    // pick up the new locale via getLocale() naturally.
    if (phasesLoaded) {
      try { await dispatch(assessmentLoad()); } catch (e) { /* fallthrough */ }
    }
  };

  return (
    <Wrap>
      <FormControl size="small">
        <StyledSelect
          value={lang}
          onChange={handleChange}
          inputProps={{ 'aria-label': 'Language' }}
        >
          {ASSESSMENT_LANGS.map((l) => (
            <MenuItem key={l.code} value={l.code}>
              {l.label}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
    </Wrap>
  );
}

// Assessment language picker is Ukrainian/Russian/English only — that's
// the profitroll.com.ua audience. On jobalto.com the locale is already
// pinned by the URL prefix (/en-gb/, /de-de/, ...) and the assessment
// strings aren't translated to those locales anyway, so the dropdown
// would only confuse. Single-point gate so all 8 call sites in
// Assessment/index.jsx are covered without per-site conditionals.
// `config.brand` is a build-time constant (one of 'profitroll'/'jobalto'),
// so the gated branch is dead-code-eliminated in each brand's bundle.
export default function LanguageDropdown() {
  if (config.brand === 'jobalto') return null;
  return <LanguageDropdownInner />;
}
